/**
 * @file: useRecentServiceRecords.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useServiceRecordContext } from '@euler/app/flows/record/ServiceRecordContext';
import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { VehicleServiceRecord } from '@euler/model';
import { WithExtraVehicleModelProps } from '@euler/model/WithExtraVehicleModelProps';
import { useServiceFactory } from '@euler/services/factory';
import { dateFromValue } from '@euler/utils/datetime';
import { useMemo } from 'react';

type RecordList = WithExtraVehicleModelProps<VehicleServiceRecord>[];

const kExpiry = 7 * 24 * 3600;
const kLimit = 10;

export const useRecentServiceRecords = () => {
  const {
    serviceRecord: { _id, licensePlateNo, vin, createdAt },
  } = useServiceRecordContext();
  const endDate = dateFromValue(createdAt).toISOString();
  const filename = `${_id}-${endDate.replace(/-/g, '')}.json`;
  const cachedPath = useMemo(
    () => ['service-records', 'ancestors', filename].join('/'),
    [filename],
  );

  const { serviceRecordService } = useServiceFactory();

  const { state, invalidate } = useCachedQuery<RecordList>(
    cachedPath,
    kExpiry,
    () =>
      serviceRecordService
        .search(
          {
            licensePlateNo,
            vin,
            endDate,
            endInclusive: false,
          },
          {
            items: true,
            events: false,
            comment: false,
            pendingIssues: false,
            troubleCodes: false,
            userMedias: false,
          },
          0,
          kLimit,
        )
        .then(res => res.filter(x => x._id !== _id)),
  );
  return [state, invalidate] as const;
};
